import React, { useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  BookMarked,
  ChevronRight,
  Download,
  ExternalLink,
  Folder as FolderIcon,
  FolderOpen,
  Inbox,
  Settings2,
  Upload,
} from 'lucide-react';
import type { DesignerStandardItem, ProjectFile } from '../../types';
import {
  bucketUnversionedFiles,
  DESIGNER_STANDARDS_ROOT_PATH,
  findProjectFolder,
  flattenProjectFolders,
  projectFolderBreadcrumb,
  projectFoldersForViewer,
  UNSORTED_FOLDER_LABEL,
  UNSORTED_FOLDER_PATH,
  type ProjectFolderDef,
} from '../../utils/projectFileFolders';
import { designerStandardsInSubfolder } from '../../utils/designerStandardsService';
import ProjectFilesUnversioned, { type ProjectFilesUnversionedProps } from './ProjectFilesUnversioned';
import './ProjectFilesViews.css';

export interface ProjectFoldersViewProps {
  projectId: string;
  dealId?: string;
  /** Unversioned files only (parent already filters `!streamId`). */
  files: ProjectFile[];
  designerStandards: DesignerStandardItem[];
  designerStandardsError: string | null;
  canMutate: boolean;
  canManageStandards: boolean;
  onUploadToFolder: (folderPath: string | null) => void;
  onMoveToFolder: (file: ProjectFile, folderPath: string | null) => void;
  onOpenSettingsStandards?: () => void;
  /** Row rendering handlers shared with the classic list. */
  rowProps: Pick<
    ProjectFilesUnversionedProps,
    | 'fileHasUnreadFileComment'
    | 'getFileCommentCount'
    | 'onOpenPdf'
    | 'onOpenImage'
    | 'onOpenCsv'
    | 'onOpenHtml'
    | 'onDownload'
    | 'onDelete'
    | 'deletingId'
  >;
  /** Folder tree for this viewer. Financials is omitted unless the caller includes it. */
  folderTree?: ProjectFolderDef[];
  /** Sidebar heading. Architects use "Other files". */
  title?: string;
  /** Initial selected folder (e.g. from upload success). */
  initialFolderPath?: string | null;
  /** Fires with the writable folder path (or null for Unsorted / Designer Standards) whenever selection changes. */
  onSelectedFolderChange?: (folderPath: string | null) => void;
}

const SELECTED_KEY = (projectId: string) => `pf:${projectId}:folder`;

function formatSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ProjectFoldersView: React.FC<ProjectFoldersViewProps> = ({
  projectId,
  dealId,
  files,
  designerStandards,
  designerStandardsError,
  canMutate,
  canManageStandards,
  onUploadToFolder,
  onMoveToFolder,
  onOpenSettingsStandards,
  rowProps,
  folderTree = projectFoldersForViewer(false),
  title = 'Project folders',
  initialFolderPath,
  onSelectedFolderChange,
}) => {
  const includeEmployeeOnly = folderTree.some(f => f.employeeOnly);
  const buckets = useMemo(
    () => bucketUnversionedFiles(files, { includeEmployeeOnly }),
    [files, includeEmployeeOnly],
  );
  const standardsCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const def of folderTree.find(f => f.path === DESIGNER_STANDARDS_ROOT_PATH)?.children ?? []) {
      if (!def.designerStandardSubfolder) continue;
      const n = designerStandardsInSubfolder(designerStandards, def.designerStandardSubfolder).length;
      m.set(def.path, n);
      m.set(DESIGNER_STANDARDS_ROOT_PATH, (m.get(DESIGNER_STANDARDS_ROOT_PATH) ?? 0) + n);
    }
    return m;
  }, [designerStandards, folderTree]);

  const [selected, setSelected] = useState<string>(() => {
    if (initialFolderPath !== undefined && initialFolderPath !== null) return initialFolderPath;
    if (typeof window === 'undefined') return UNSORTED_FOLDER_PATH;
    try {
      return window.localStorage.getItem(SELECTED_KEY(projectId)) || UNSORTED_FOLDER_PATH;
    } catch {
      return UNSORTED_FOLDER_PATH;
    }
  });
  useEffect(() => {
    if (initialFolderPath) setSelected(initialFolderPath);
  }, [initialFolderPath]);
  useEffect(() => {
    try {
      window.localStorage.setItem(SELECTED_KEY(projectId), selected);
    } catch {
      /* ignore */
    }
  }, [projectId, selected]);
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(folderTree.map(f => f.path)),
  );
  useEffect(() => {
    setExpanded(prev => {
      let changed = false;
      const next = new Set(prev);
      for (const folder of folderTree) {
        if (!next.has(folder.path)) {
          next.add(folder.path);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [folderTree]);
  const toggleExpanded = (path: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  // Unsorted only exists while legacy / unfiled uploads remain; once filed it disappears.
  const showUnsorted = buckets.unsorted.length > 0;
  useEffect(() => {
    if (!showUnsorted && selected === UNSORTED_FOLDER_PATH) {
      setSelected(folderTree[0]?.path ?? UNSORTED_FOLDER_PATH);
    }
  }, [showUnsorted, selected, folderTree]);

  const selectedDef = selected === UNSORTED_FOLDER_PATH
    ? null
    : flattenProjectFolders(folderTree).find(f => f.path === selected) ?? null;
  useEffect(() => {
    if (selected === UNSORTED_FOLDER_PATH || selectedDef) return;
    setSelected(showUnsorted ? UNSORTED_FOLDER_PATH : folderTree[0]?.path ?? UNSORTED_FOLDER_PATH);
  }, [selected, selectedDef, showUnsorted, folderTree]);
  const isUnsorted = selected === UNSORTED_FOLDER_PATH;
  const isStandards = !!selectedDef?.designerStandards;
  useEffect(() => {
    onSelectedFolderChange?.(isUnsorted || isStandards || !selectedDef ? null : selected);
  }, [selected, isUnsorted, isStandards, selectedDef, onSelectedFolderChange]);

  const countFor = (path: string): number =>
    path === UNSORTED_FOLDER_PATH
      ? buckets.unsorted.length
      : findProjectFolder(path)?.designerStandards
        ? standardsCounts.get(path) ?? 0
        : buckets.counts.get(path) ?? 0;

  const renderTreeNode = (def: ProjectFolderDef, depth: number) => {
    const hasChildren = !!def.children?.length;
    const open = expanded.has(def.path);
    const active = selected === def.path;
    const count = countFor(def.path);
    return (
      <li key={def.path} className="pff-tree-li">
        <div
          className={`pff-tree-row${active ? ' pff-tree-row--active' : ''}`}
          style={{ paddingLeft: 8 + depth * 14 }}
          data-tour={def.path === DESIGNER_STANDARDS_ROOT_PATH ? 'pf-designer-standards' : undefined}
        >
          {hasChildren ? (
            <button
              type="button"
              className="pff-tree-caret"
              aria-label={open ? `Collapse ${def.label}` : `Expand ${def.label}`}
              onClick={() => toggleExpanded(def.path)}
            >
              <ChevronRight size={12} className={`pfv-caret${open ? ' pfv-caret--open' : ''}`} aria-hidden />
            </button>
          ) : (
            <span className="pff-tree-caret pff-tree-caret--spacer" aria-hidden />
          )}
          <button type="button" className="pff-tree-btn" onClick={() => setSelected(def.path)}>
            {def.designerStandards ? (
              <BookMarked size={13} aria-hidden />
            ) : active ? (
              <FolderOpen size={13} aria-hidden />
            ) : (
              <FolderIcon size={13} aria-hidden />
            )}
            <span className="pff-tree-label">{def.label}</span>
            {count > 0 ? <span className="pff-tree-count">{count}</span> : null}
          </button>
        </div>
        {hasChildren && open ? (
          <ul className="pff-tree">{def.children!.map(c => renderTreeNode(c, depth + 1))}</ul>
        ) : null}
      </li>
    );
  };

  const directFiles = isUnsorted ? buckets.unsorted : buckets.byFolder.get(selected) ?? [];
  const childDefs = selectedDef?.children ?? [];

  const renderStandards = () => {
    const defs = selectedDef?.designerStandardSubfolder
      ? [selectedDef]
      : folderTree.find(f => f.path === DESIGNER_STANDARDS_ROOT_PATH)?.children ?? [];
    const anyItems = defs.some(
      d => d.designerStandardSubfolder && designerStandardsInSubfolder(designerStandards, d.designerStandardSubfolder).length > 0,
    );
    return (
      <div className="pff-standards">
        <p className="pff-standards-note">
          <BookMarked size={13} aria-hidden /> Shared library — the same SOPs and templates appear on every
          project. {canManageStandards ? 'Manage them in Settings → Project Defaults.' : 'Managed by the office.'}
          {canManageStandards && onOpenSettingsStandards ? (
            <button type="button" className="pfv-link" onClick={onOpenSettingsStandards}>
              <Settings2 size={12} aria-hidden /> Open settings
            </button>
          ) : null}
        </p>
        {designerStandardsError ? <p className="pff-standards-error">{designerStandardsError}</p> : null}
        {!anyItems && !designerStandardsError ? (
          <div className="pfv-empty pfv-empty--compact">
            <p className="pfv-empty-title">No standards uploaded yet</p>
            <p className="pfv-empty-hint">
              {canManageStandards
                ? 'Upload SOPs, .2d / .3d templates and the timber list export in Settings → Project Defaults → Designer Standards.'
                : 'The office has not published Designer Standards yet.'}
            </p>
          </div>
        ) : null}
        {defs.map(d => {
          if (!d.designerStandardSubfolder) return null;
          const items = designerStandardsInSubfolder(designerStandards, d.designerStandardSubfolder);
          if (items.length === 0) return null;
          return (
            <div key={d.path} className="pfv-subgroup">
              {!selectedDef?.designerStandardSubfolder ? <h4 className="pfv-subgroup-title">{d.label}</h4> : null}
              <ul className="pfv-list">
                {items.map(item => (
                  <li key={item.id} className="pfv-row">
                    <span className="pfv-row-icon" aria-hidden>
                      <BookMarked size={15} />
                    </span>
                    <div className="pfv-row-main">
                      <div className="pfv-row-title-line">
                        <span className="pfv-row-title" title={item.name}>
                          {item.name}
                        </span>
                      </div>
                      <div className="pfv-row-meta">
                        {item.size ? <>{formatSize(item.size)} · </> : null}
                        {item.uploadedByName || 'Office'}
                        {item.uploadedAt.getTime() > 0 ? (
                          <> · {formatDistanceToNow(item.uploadedAt, { addSuffix: true })}</>
                        ) : null}
                      </div>
                    </div>
                    <div className="pfv-row-acts">
                      <a className="pfv-act" href={item.url} target="_blank" rel="noopener noreferrer">
                        Open <ExternalLink size={11} aria-hidden />
                      </a>
                      <a
                        className="pfv-act pfv-act--muted"
                        href={item.url}
                        download={item.name}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Download size={11} aria-hidden /> Download
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="pff" data-tour="pf-folders">
      <aside className="pff-tree-pane">
        <h3 className="pf-section-title">
          <FolderIcon size={14} /> {title}
        </h3>
        <ul className="pff-tree">
          {showUnsorted ? (
            <li className="pff-tree-li">
              <div className={`pff-tree-row${isUnsorted ? ' pff-tree-row--active' : ''}`} style={{ paddingLeft: 8 }}>
                <span className="pff-tree-caret pff-tree-caret--spacer" aria-hidden />
                <button type="button" className="pff-tree-btn" onClick={() => setSelected(UNSORTED_FOLDER_PATH)}>
                  <Inbox size={13} aria-hidden />
                  <span className="pff-tree-label">{UNSORTED_FOLDER_LABEL}</span>
                  <span className="pff-tree-count">{buckets.unsorted.length}</span>
                </button>
              </div>
            </li>
          ) : null}
          {folderTree.map(def => renderTreeNode(def, 0))}
        </ul>
      </aside>
      <section className="pff-main">
        <div className="pff-main-head">
          <div className="pff-crumb">
            <span className="pff-crumb-text">{projectFolderBreadcrumb(isUnsorted ? null : selected)}</span>
            {isUnsorted ? (
              <span className="pff-crumb-hint">Files that were uploaded before folders, or without one.</span>
            ) : null}
          </div>
          {canMutate && !isStandards ? (
            <button
              type="button"
              className="pf-upload-btn"
              onClick={() => onUploadToFolder(isUnsorted ? null : selected)}
            >
              <Upload size={13} aria-hidden /> Upload here
            </button>
          ) : null}
        </div>

        {isStandards ? (
          renderStandards()
        ) : (
          <>
            {childDefs.length > 0 ? (
              <div className="pff-children">
                {childDefs.map(c => (
                  <button key={c.path} type="button" className="pff-child-card" onClick={() => setSelected(c.path)}>
                    {c.designerStandards ? <BookMarked size={18} aria-hidden /> : <FolderIcon size={18} aria-hidden />}
                    <span className="pff-child-label">{c.label}</span>
                    <span className="pff-child-count">{countFor(c.path)}</span>
                  </button>
                ))}
              </div>
            ) : null}
            {directFiles.length > 0 ? (
              <ProjectFilesUnversioned
                projectId={projectId}
                dealId={dealId}
                files={directFiles}
                title={isUnsorted ? UNSORTED_FOLDER_LABEL : selectedDef?.label || 'Files'}
                onMoveToFolder={canMutate ? onMoveToFolder : undefined}
                readOnly={!canMutate}
                {...rowProps}
              />
            ) : childDefs.length === 0 || isUnsorted ? (
              <div className="pfv-empty pfv-empty--compact">
                <p className="pfv-empty-title">
                  {isUnsorted ? 'Everything is filed' : 'Empty folder'}
                </p>
                <p className="pfv-empty-hint">
                  {isUnsorted
                    ? 'New general uploads land here until you pick a folder.'
                    : canMutate
                      ? 'Upload here, or use “Move to folder” on a file in Unsorted.'
                      : 'Nothing has been filed here yet.'}
                </p>
              </div>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
};

export default ProjectFoldersView;
